import { EmptyState, LinkAction, Pagination, Table, type TableColumn } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { InboxRowView, Translate } from './content-types'

/**
 * Props for {@link InboxView}.
 *
 * @property rows - In-review localizations the viewer may approve, in API order.
 * @property onNavigate - Opens a row's "Compare with in force" link.
 */
export interface InboxViewProps {
	t: Translate
	rows: InboxRowView[]
	page: number
	pageCount: number
	empty: boolean
	error: boolean
	isBusy: boolean
	onApprove: (localizationId: string) => void
	onReject: (localizationId: string) => void
	onNavigate: (href: string) => void
	onPageChange: (page: number) => void
	onRetry: () => void
}

const columns = (props: InboxViewProps): TableColumn<InboxRowView>[] => {
	const { t } = props
	return [
		{ key: 'textCode', header: t('inbox.columns.text') },
		{ key: 'title', header: t('inbox.columns.title') },
		{ key: 'locale', header: t('inbox.columns.language') },
		{ key: 'versionNumber', header: t('inbox.columns.version'), cell: (row) => `v${row.versionNumber}` },
		{
			key: 'author',
			header: t('inbox.columns.author'),
			cell: (row) => (row.authorYou ? t('track.you') : row.authorShort),
		},
		{ key: 'submittedAt', header: t('inbox.columns.submitted') },
		{
			key: 'compare',
			header: t('inbox.columns.compare'),
			cell: (row) =>
				row.compareHref ? (
					<LinkAction onPress={() => props.onNavigate(row.compareHref as string)}>{t('inbox.compare')}</LinkAction>
				) : (
					t('inbox.firstPublication')
				),
		},
		{
			key: 'actions',
			header: t('inbox.columns.actions'),
			cell: (row) => (
				<span className="flex gap-2">
					<Button size="sm" isDisabled={props.isBusy} onPress={() => props.onApprove(row.localizationId)}>
						{t('lifecycle.approve')}
					</Button>
					<Button
						size="sm"
						variant="danger"
						isDisabled={props.isBusy}
						onPress={() => props.onReject(row.localizationId)}
					>
						{t('lifecycle.reject')}
					</Button>
				</span>
			),
		},
	]
}

/** The approval inbox: what the viewer may approve, with approve/reject in place (organism). */
export function InboxView(props: InboxViewProps): ReactElement {
	const { t } = props
	let body: ReactElement
	if (props.error) {
		body = <EmptyState title={t('inbox.error')} action={{ label: t('actions.retry'), onPress: props.onRetry }} />
	} else if (props.empty) {
		body = <EmptyState title={t('inbox.empty')} />
	} else {
		body = <Table columns={columns(props)} rows={props.rows} />
	}
	return (
		<section className="flex flex-col gap-4">
			<h1 className="text-ds-xl font-semibold text-ds-text">{t('inbox.title')}</h1>
			{body}
			<Pagination page={props.page} pageCount={props.pageCount} onPageChange={props.onPageChange} />
		</section>
	)
}
